enum CacheType {
  Local,
  Session
}

class Cache {
  storage: Storage

  constructor(type: CacheType) {
    this.storage = type === CacheType.Local ? localStorage : sessionStorage
  }

  /**
   * 获取所有缓存的key
   */
  getAllKeys(): string[] {
    const keys: string[] = []
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i)
      if (key) {
        keys.push(key)
      }
    }
    return keys
  }

  /**
   * 获取指定前缀的所有key
   */
  getKeysByPrefix(prefix: string): string[] {
    return this.getAllKeys().filter((key) => key.startsWith(prefix))
  }

  /**
   * 检查key是否存在
   */
  hasKey(key: string): boolean {
    return this.storage.getItem(key) !== null
  }

  /**
   * 获取所有缓存的key-value对
   */
  getAllEntries(): [string, any][] {
    const entries: [string, any][] = []
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i)
      if (key) {
        const value = this.getCache(key)
        entries.push([key, value])
      }
    }
    return entries
  }

  /**
   * 获取缓存数量
   */
  getSize(): number {
    return this.storage.length
  }

  /**
   * 存储缓存
   * 对字符串类型直接存储，其他类型JSON序列化后存储
   */
  setCache(key: string, value: any) {
    if (value === undefined || value === null) return

    if (typeof value === 'string') {
      // 字符串类型直接存储，避免双重JSON序列化
      this.storage.setItem(key, value)
    } else {
      // 其他类型（对象、数组等）JSON序列化后存储
      this.storage.setItem(key, JSON.stringify(value))
    }
  }

  /**
   * 获取缓存
   * 尝试JSON解析，失败则返回原始字符串（适用于日期字符串等）
   */
  getCache<T = any>(key: string): T | string | null {
    const value = this.storage.getItem(key)
    if (!value) return null

    try {
      // 尝试JSON解析（处理对象、数组等类型）
      return JSON.parse(value)
    } catch (e) {
      // 解析失败，说明是字符串类型，直接返回
      return value
    }
  }

  /**
   * 获取缓存并指定类型
   */
  getCacheWithType<T>(key: string): T | null {
    const value = this.getCache(key)
    return value as T
  }

  /**
   * 安全删除缓存（防止删除不存在的key报错）
   */
  removeCache(key: string) {
    console.log(`output->key`, key)
    this.storage.removeItem(key)
  }

  /**
   * 批量删除缓存
   */
  removeMultiple(keys: string[]) {
    keys.forEach((key) => this.removeCache(key))
  }

  /**
   * 删除指定前缀的所有缓存
   */
  removeByPrefix(prefix: string) {
    const keys = this.getKeysByPrefix(prefix)
    this.removeMultiple(keys)
  }

  clear() {
    this.storage.clear()
  }

  /**
   * 获取缓存值并设置过期时间
   */
  setCacheWithExpiry(key: string, value: any, expiryInSeconds: number) {
    const now = new Date()
    const item = {
      value: value,
      expiry: now.getTime() + expiryInSeconds * 1000
    }
    this.setCache(key, item)
  }

  /**
   * 获取带过期时间的缓存
   */
  getCacheWithExpiry(key: string) {
    const item = this.getCache<{ value: any; expiry: number }>(key)

    if (!item) return null

    const now = new Date().getTime()

    // 检查是否过期
    if (now > item.expiry) {
      this.removeCache(key)
      return null
    }

    return item.value
  }

  /**
   * 缓存已用字节数（近似值）
   */
  getUsedSpace(): number {
    let total = 0
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i)
      if (key) {
        const value = this.storage.getItem(key)
        if (value) {
          total += key.length + value.length
        }
      }
    }
    return total * 2 // UTF-16 字符占用2字节
  }

  /**
   * 清空过期缓存
   */
  clearExpired() {
    const keys = this.getAllKeys()
    keys.forEach((key) => {
      const value = this.getCache(key)
      if (value && typeof value === 'object' && 'expiry' in value) {
        const expiry = (value as any).expiry
        if (new Date().getTime() > expiry) {
          this.removeCache(key)
        }
      }
    })
  }
}

const localCache = new Cache(CacheType.Local)
const sessionCache = new Cache(CacheType.Session)

export { CacheType, localCache, sessionCache }
